import { NoData, Pending, useErrorState, usePendingState } from '@finbeat/react-toolkit';
import { AxiosError } from 'axios';
import { getOrderGeneralInfo } from 'axios/orderApi';
import useApi from 'hooks/useApi';
import { AxiosErrorResponse } from 'models/api/axiosErrorResponse';
import { OrderDetailsGeneralInfo } from 'models/api/order';
import { OrderInfoProvider, useOrderInfoContext } from 'providers/OrderInfoProvider';
import { RefObject, useEffect, useState } from 'react';
import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import OrderInfo from './OrderInfo/OrderInfo';
import OrderUnsavedChangesConfirm from './OrderUnsavedChangesConfirm';

type OrderInfoProps = {
    id: number | string;
};

export const useOrderGeneralInfo = (id?: number | string) => {
    const api = useApi();
    const [orderDetails, setOrderDetails] = useState<OrderDetailsGeneralInfo | null>(null);
    const { handleRequestError } = useErrorState();
    const { setPending } = usePendingState();

    const { isFetching } = useQuery(
        ['orderDetail', id],
        () => (id ? getOrderGeneralInfo(api, id) : undefined),
        {
            onSuccess: (data) => data && setOrderDetails(data),
            enabled: !orderDetails,
            retry: (failureCount, error: AxiosError<AxiosErrorResponse>) => {
                return (
                    failureCount < 5 &&
                    error.response?.data.errorCode !== 'BackOfficeErrorOrderInfoUnavailable'
                );
            },
            onError: (e) => handleRequestError(e),
        }
    );

    useEffect(() => setPending(isFetching), [isFetching]);

    return { isFetching, orderDetails };
};

const OrderInfoData = ({ id }: OrderInfoProps) => {
    const { isFetching, orderDetails } = useOrderGeneralInfo(id);
    const navigate = useNavigate();

    return (
        <OrderInfoProvider
            orderDetails={orderDetails}
            onSubmitOrderSuccess={() => navigate('/order')}
        >
            {!isFetching ? <Info /> : <Pending />}
        </OrderInfoProvider>
    );
};

export const Info = ({ ...props }: { isModal?: boolean; modalRef?: RefObject<HTMLElement> }) => {
    const { orderDetails, isDirty } = useOrderInfoContext();
    const [isOpenNotify, setOpenNotify] = useState(false);
    const navigate = useNavigate();

    const toggleModal = () => {
        isDirty ? setOpenNotify((prev) => !prev) : navigate('/order');
    };

    return (
        <>
            {orderDetails ? (
                <OrderInfo generalInfo={orderDetails} cancelAction={toggleModal} {...props} />
            ) : (
                <NoData />
            )}
            <OrderUnsavedChangesConfirm
                isOpen={isOpenNotify}
                closeNotify={() => setOpenNotify(false)}
                closeWindow={() => navigate('/order')}
            />
        </>
    );
};

export default OrderInfoData;
